import { datadogRum } from "@datadog/browser-rum";

import { AppletProps, QueryState, QueryStateCustomAttributeNameMap } from "./types";

import { Paths } from "~/OmnichannelApi/openapi";

export const extractSearchParamsFromAttributes = (el: HTMLDivElement): AppletProps => {
  const getAttr = (attr: string, defaultValue?: string) => el.getAttribute(attr) || defaultValue;
  const getBooleanAttr = (attr: string, defaultValue: true | false) =>
    Boolean(getAttr(attr, String(defaultValue)) === "true");

  const locationSlug = getAttr("locationslug");

  const commonProps = {
    headerLevel: Number(getAttr("headerlevel", "1")) as AppletProps["headerLevel"],
    clearAllOption: getBooleanAttr("clearAllOption", true),
    locationSlug,
  }

  if (el.id.includes("job-query-panel")) {
    return {
      ...commonProps,
      inlineSearchParams: undefined,
      searchParams: {} as Record<string, unknown>,
      header: false,
      headerText: "",
      instantSearch: false,
      primaryControls: true,
      facets: false,
      searchResults: false,
    };
  }

  return {
    ...commonProps,
    inlineSearchParams: getAttr("searchparams")
      ? Object.fromEntries(new URLSearchParams(getAttr("searchparams")!).entries())
      : undefined,
    searchParams: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
    header: getBooleanAttr("header", true),
    headerText: getAttr("headertext", "Current Openings")!,
    instantSearch: getBooleanAttr("instantsearch", true),
    primaryControls: getBooleanAttr("primarycontrols", !locationSlug),
    facets: getBooleanAttr("facets", false),
    searchResults: getBooleanAttr("searchresults", true),
  };
};

export const buildSearchParamsFromState = (queryState: QueryState) => {
  const params = new URLSearchParams(window.location.search);
  (Object.keys(queryState) as Array<keyof QueryState>).forEach((k) => {
    if (!["", "false", false].includes(queryState[k])) {
      params.set(k, String(queryState[k]));
    } else {
      params.delete(k);
    }
  });

  return params;
};

export const buildQueryParams = (queryState: QueryState, options?: {take?: number, pageToken?: string}) => {
  const queryParams: Paths.JobsList.QueryParameters & Paths.JobsList.HeaderParameters = {
    take: options?.take || 10,
    pageToken: options?.pageToken || undefined,
  };

  const customAttributes: Array<string> = [];

  if (queryState.locationSlug) {
    queryParams.locationSlug = queryState.locationSlug;
  }

  if (queryState.q) {
    queryParams.q = queryState.q;
  }

  if (queryState.location) {
    customAttributes.push(`${QueryStateCustomAttributeNameMap.location}="${queryState.location.replace('"', '"')}"`);
  }

  if (queryState.employmentType) {
    customAttributes.push(
      `${QueryStateCustomAttributeNameMap.employmentType}="${queryState.employmentType.replace('"', '"')}"`
    );
  }

  if (queryState.department) {
    customAttributes.push(
      `${QueryStateCustomAttributeNameMap.department}="${queryState.department.replace('"', '"')}"`
    );
  }

  if (queryState.marketIds) {
    customAttributes.push(
      `(${queryState.marketIds
        .split(",")
        .map((marketId) => `${QueryStateCustomAttributeNameMap.marketIds}="${marketId}"`)
        .join(" OR ")})`
    );
  }

  if (customAttributes.length) {
    queryParams.customAttributeFilter = customAttributes.join(" AND ");
  }

  if (queryState.commuteFilter && queryState.commutePlaceId) {
    queryParams.commuteLat = Number(queryState.commuteLat);
    queryParams.commuteLon = Number(queryState.commuteLon);
    queryParams.commuteSeconds = 3600;
  }

  if (queryState.jobType) {
    queryParams.work_environment = queryState.jobType;
  }

  if (queryState.jobLocationAddress && queryState.jobLocationLat && queryState.jobLocationLon) {
    queryParams.lat = Number(queryState.jobLocationLat);
    queryParams.lon = Number(queryState.jobLocationLon);
    queryParams.address = queryState.jobLocationAddress;
    queryParams.radius = 100;
  }

  queryParams["session-id"] = datadogRum.getInternalContext()?.session_id;

  return queryParams;
};
